import { useEffect, useState } from 'react';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import { setInitialCohortBuilderSliceState } from '@verantos-dev/app-state-cohort-builder';
import {
  setCohortDefinitionLegacy,
  setCohortDefinitionTransitionStates,
  setCohortDefinitionVersionTransitionStates,
} from '@verantos-dev/app-state-cohort-definition';
import { useAuth } from '@verantos-dev/core-auth';
import {
  transformCohortDSLStringToCohortBuilderDefinition,
  useGetCohortQuery,
  useGetCohortVersionQuery,
} from '@verantos-dev/data-access-cohorts';
import { useConceptGroups } from '@verantos-dev/data-access-concept-groups';
import { CohortStatusLegacy } from '@verantos-dev/types-cohorts';
import { generateUIModelFromDSL } from '@verantos-dev/util-cohort-definition-dsl-to-ui';

export const usePrefetchCohortData = (
  projectId: string | undefined,
  cohortId: string | undefined,
  cohortVersionId: string | undefined
) => {
  const dispatch = useAppDispatch();

  /** Handle prefetching cohort data */

  // Fetch authenticated user
  const {
    user: { auth0Id, userId },
  } = useAuth();
  const [latestStatus, setLatestStatus] = useState<string | undefined>(undefined);

  // fetch cohort profile data
  const {
    data: cohortData,
    isLoading: cohortIsLoading,
    isUninitialized: cohortIsUninitialized,
    error: cohortError,
    status,
    refetch: refetchCohort,
  } = useGetCohortQuery(
    { projectId: projectId || '', cohortId: cohortId || '' },
    {
      skip: !projectId || !cohortId,
      pollingInterval: latestStatus === CohortStatusLegacy.PROCESSING ? 5000 : 0, // 5 seconds
    }
  );
  const isCohortProfileLoading = cohortIsLoading || cohortIsUninitialized;

  // Update latestStatus state whenever cohortData changes
  useEffect(() => {
    if (cohortData?.data.latestStatus) {
      setLatestStatus(cohortData.data.latestStatus);
    }
  }, [cohortData?.data.latestStatus]);

  // Restart polling when status changes to PROCESSING
  useEffect(() => {
    if (latestStatus === CohortStatusLegacy.PROCESSING) {
      refetchCohort();
    }
  }, [latestStatus, refetchCohort]);

  // fetch cohort version data
  const {
    data: versionData,
    isFetching: versionFetching,
    isLoading: versionLoading,
    isUninitialized: versionUninitialized,
    error: versionError,
    status: versionStatus,
  } = useGetCohortVersionQuery(
    {
      projectId: projectId || '',
      cohortId: cohortId || '',
      cohortVersionId: cohortVersionId || '',
    },
    {
      skip: !projectId || !cohortId || !cohortVersionId,
    }
  );
  const isCohortVersionLoading = versionFetching || versionLoading || versionUninitialized;

  // Fetch concept groups
  useConceptGroups();

  // handle cohort profile data fetching transition states
  useEffect(() => {
    if (cohortError) {
      dispatch(
        setCohortDefinitionTransitionStates({
          isLoading: false,
          error: cohortError,
        })
      );
    }
  }, [cohortError, dispatch, isCohortProfileLoading]);

  // handle cohort version data fetching transition states
  useEffect(() => {
    if (versionError) {
      dispatch(
        setCohortDefinitionVersionTransitionStates({
          isLoading: false,
          error: versionError,
        })
      );
    }
  }, [versionError, dispatch, isCohortVersionLoading]);

  // clear cohort profile loading state when data is fetched
  useEffect(() => {
    if (status === 'fulfilled') {
      dispatch(
        setCohortDefinitionTransitionStates({
          isLoading: false,
          error: cohortError,
        })
      );
    }
  }, [cohortError, dispatch, status]);

  // clear cohort version loading state when
  useEffect(() => {
    if (versionStatus === 'fulfilled') {
      dispatch(
        setCohortDefinitionVersionTransitionStates({
          isLoading: false,
          error: versionError,
        })
      );
    }
  }, [dispatch, versionError, versionStatus]);

  // Set staged cohort definition from the fetched data
  useEffect(() => {
    // Set cohort profile state when data is fetched
    if (cohortData?.data?.createdBy.userId && versionData?.data?.dslValue) {
      console.info('Generating & setting UI model from persisted model');
      const cohortDsl = versionData?.data?.dslValue;

      // Legacy runtime hook into cohort builder
      dispatch(
        setInitialCohortBuilderSliceState(
          transformCohortDSLStringToCohortBuilderDefinition(cohortDsl)
        )
      );
      dispatch(setCohortDefinitionVersionTransitionStates({ isLoading: false, error: undefined }));

      // context for top level cohort definition workflow
      const isEditable = [auth0Id, userId].includes(cohortData?.data?.createdBy.userId);
      dispatch(
        setCohortDefinitionLegacy({
          cohortDefinitionLegacy: generateUIModelFromDSL(cohortDsl),
          isStagedFromResearchAssistant: false,
          isEditable,
        })
      );

      console.info('Setting cohort profile state from fetched data');
      dispatch(
        setCohortDefinitionTransitionStates({
          isLoading: false,
          error: undefined,
        })
      );
    }
  }, [auth0Id, cohortData?.data?.createdBy.userId, dispatch, userId, versionData?.data?.dslValue]);

  return {
    cohortData,
  };
};
